import React from 'react';
import {
  Container, Box, Typography, Grid, Card, CardContent, Paper,
  Button, Avatar, Chip, Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Security, School, Group, Person, Shield, Psychology,
  TrendingUp, Verified, WorkspacePremium, Handshake
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { navigateAndScroll } from '../utils/navigation';
import { getActiveColleges } from '../data/colleges';
import CollegeLogo from '../components/common/CollegeLogo';
import photos from '../data/photos';
import SEO from '../components/common/SEO';

const HeroSection = styled(Box)({
  backgroundImage: `linear-gradient(rgba(30,58,138,0.82), rgba(124,58,237,0.82)), url("${photos.heroes.about}")`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  color: 'white',
  padding: '80px 0',
  textAlign: 'center',
});

const ValueCard = styled(Card)(({ theme }) => ({
  height: '100%',
  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
  '&:hover': { transform: 'translateY(-5px)', boxShadow: theme.shadows[8] },
}));

const IconBox = styled(Box)({
  width: 80, height: 80, borderRadius: 20,
  background: 'linear-gradient(135deg, #60a5fa, #7c3aed)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  color: 'white', margin: '0 auto 1.5rem',
});

function AboutPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const partnerColleges = getActiveColleges();

  return (
    <Box>
      <SEO title="About Us" description="Meet the CISSP-certified expert team behind Cyber Wise Corp. 10+ years experience mentoring college interns." />
      {/* Hero */}
      <HeroSection>
        <Container maxWidth="lg">
          <Chip label="Who We Are" sx={{ mb: 3, backgroundColor: 'rgba(255,255,255,0.15)', color: 'white' }} />
          <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
            A Team of Experts on a Mission
          </Typography>
          <Typography variant="h5" sx={{ opacity: 0.95, maxWidth: 750, mx: 'auto' }}>
            Cyber Wise Corp is built by seasoned cybersecurity professionals who believe expert security guidance should be accessible to everyone, not just corporations with six-figure budgets.
          </Typography>
        </Container>
      </HeroSection>

      {/* The Problem We Solve */}
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Typography variant="h3" component="h2" textAlign="center" gutterBottom sx={{ fontWeight: 700 }}>
          The Problem We Solve
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, fontSize: '1.1rem', lineHeight: 1.8 }}>
          Traditional cybersecurity assessments cost $1,000 to $5,000 and are priced for enterprise budgets, not families and small businesses. Yet homes today run 15-20 connected devices on average, and small businesses are the #1 target for cybercriminals precisely because they lack professional security guidance.
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, fontSize: '1.1rem', lineHeight: 1.8 }}>
          At the same time, thousands of talented cybersecurity students graduate every year with classroom knowledge but no real-world experience, which is the one thing every employer demands. Colleges teach theory. The industry wants practitioners.
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem', lineHeight: 1.8, fontWeight: 500 }}>
          Cyber Wise Corp solves both problems at once. Our team of CISSP-certified experts supervises and mentors college interns who perform hands-on security assessments at an affordable price. Clients get an expert-reviewed security assessment for $249.99. Students get the field experience that launches careers. Everyone wins.
        </Typography>
      </Container>

      {/* Our Expert Team */}
      <Box sx={{ backgroundColor: 'grey.50', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" textAlign="center" gutterBottom sx={{ fontWeight: 700 }}>
            Our Expert Review Team
          </Typography>
          <Typography variant="h6" textAlign="center" color="text.secondary" sx={{ mb: 2, maxWidth: 700, mx: 'auto' }}>
            Every CWC assessment is reviewed by a certified cybersecurity professional before it reaches you. Our experts don't just check boxes. They mentor the next generation while ensuring your report meets the highest standards.
          </Typography>

          {/* Expert qualifications */}
          <Grid container spacing={3} sx={{ mt: 4 }}>
            {[
              { photo: photos.about.cissp, title: 'CISSP Certified', desc: 'Every reviewing expert holds the CISSP, the gold standard certification in cybersecurity, requiring 5+ years of professional experience and ongoing continuing education.' },
              { photo: photos.about.credentials, title: 'Stacked Credentials', desc: 'Our experts carry additional certifications including CCSP, CISM, CEH, OSCP, CompTIA CASP+, and more. Most hold 3-5 active certifications across multiple security domains.' },
              { photo: photos.about.experienced, title: '10+ Years Minimum', desc: 'Every expert on our team has a minimum of 10 years of hands-on cybersecurity experience spanning penetration testing and incident response to GRC, cloud security, and executive risk management.' },
              { photo: photos.about.mentors, title: 'Active Mentors', desc: 'Our experts are not just reviewers. They are mentors. They provide detailed feedback on every assessment, coaching interns on what they missed, what they got right, and how to improve.' },
            ].map((item, i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <ValueCard>
                  <Box sx={{ width: '100%', height: 160, overflow: 'hidden' }}>
                    <Box component="img" src={item.photo} alt={item.title}
                      sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </Box>
                  <CardContent sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>{item.title}</Typography>
                    <Typography variant="body2" color="text.secondary">{item.desc}</Typography>
                  </CardContent>
                </ValueCard>
              </Grid>
            ))}
          </Grid>

          {/* Why experts join CWC */}
          <Paper elevation={2} sx={{ p: 4, mt: 6, borderRadius: 4, border: '2px solid', borderImage: 'linear-gradient(135deg, #2563eb, #7c3aed) 1' }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, textAlign: 'center' }}>
              Why Top Experts Choose CWC
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', mb: 3, maxWidth: 700, mx: 'auto', lineHeight: 1.8 }}>
              We are actively engaging cybersecurity professionals across the industry who want to be part of something meaningful. Our experts are drawn to CWC because we offer a rare combination:
            </Typography>
            <Grid container spacing={2}>
              {[
                'Give back to the community by making security accessible to families and small businesses',
                'Mentor the next generation of cybersecurity professionals at Arizona colleges',
                'Get paid for each assessment reviewed, with a flexible, part-time commitment',
                'Help colleges build stronger cybersecurity programs through real-world feedback',
                'Be part of a mission-driven team that is changing how security services are delivered',
              ].map((item, i) => (
                <Grid item xs={12} sm={6} key={i}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <Verified sx={{ color: '#7c3aed', mt: 0.3, flexShrink: 0 }} />
                    <Typography variant="body1">{item}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Container>
      </Box>

      {/* How It Works */}
      <Box sx={{ backgroundColor: 'grey.50', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" textAlign="center" gutterBottom sx={{ fontWeight: 700 }}>
            The CWC Difference
          </Typography>
          <Typography variant="h6" textAlign="center" color="text.secondary" sx={{ mb: 6 }}>
            A dual-layer approach that combines fresh talent with seasoned expertise
          </Typography>
          <Grid container spacing={4}>
            {[
              { photo: photos.about.internPerforms, title: 'Intern Performs', desc: 'A vetted college cybersecurity student conducts your 1-hour assessment using our comprehensive checklist. They bring current training, technical skills, and genuine attention to detail.' },
              { photo: photos.about.expertReviews, title: 'Expert Reviews', desc: 'A CISSP-certified professional with 10+ years of experience reviews every finding, validates the scoring, and adds additional recommendations based on their deep experience.' },
              { photo: photos.about.mentorCoaches, title: 'Mentor Coaches', desc: 'The expert provides detailed feedback to the intern: what they caught, what they missed, and how to improve. Every assessment makes our interns better, which makes your next assessment even more thorough.' },
            ].map((item, i) => (
              <Grid item xs={12} md={4} key={i}>
                <ValueCard>
                  <Box sx={{ width: '100%', height: 200, overflow: 'hidden' }}>
                    <Box component="img" src={item.photo} alt={item.title}
                      sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </Box>
                  <CardContent sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>{item.title}</Typography>
                    <Typography variant="body1" color="text.secondary">{item.desc}</Typography>
                  </CardContent>
                </ValueCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Colleges */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" component="h2" textAlign="center" gutterBottom sx={{ fontWeight: 700 }}>
          Where We Recruit
        </Typography>
        <Typography variant="h6" textAlign="center" color="text.secondary" sx={{ mb: 6 }}>
          Working with Arizona's top cybersecurity programs to develop real-world talent
        </Typography>
        <Grid container spacing={3}>
          {partnerColleges.map((college) => (
            <Grid item xs={12} sm={6} md={3} key={college.id}>
              <ValueCard>
                <CardContent sx={{ p: 3, textAlign: 'center' }}>
                  <CollegeLogo college={college} height={120} />
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>{college.name}</Typography>
                  <Chip label={college.location} size="small" sx={{ mb: 1 }} />
                  {college.founderAlmaMater && (
                    <Chip label="Founder's Alma Mater" size="small" color="secondary" sx={{ ml: 0.5, mb: 1 }} />
                  )}
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{college.description}</Typography>
                </CardContent>
              </ValueCard>
            </Grid>
          ))}
        </Grid>
        <Typography variant="body1" textAlign="center" color="text.secondary" sx={{ mt: 4 }}>
          More colleges coming soon across the Southwest and nationally.{' '}
          <Button variant="text" onClick={() => navigateAndScroll(navigate, '/contact', location.pathname)} sx={{ fontWeight: 600 }}>
            Interested in partnering? Contact us.
          </Button>
        </Typography>
      </Container>

      {/* CTA */}
      <Box sx={{ backgroundImage: `linear-gradient(rgba(30,58,138,0.85), rgba(124,58,237,0.85)), url("${photos.heroes.landing}")`,
        backgroundSize: 'cover', backgroundPosition: 'center', py: 8 }}>
        <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
          <Typography variant="h3" color="white" gutterBottom sx={{ fontWeight: 700 }}>
            Ready to Get Cyber Wise?
          </Typography>
          <Typography variant="h6" color="white" sx={{ mb: 4, opacity: 0.9 }}>
            Assess your home or business with an expert-reviewed security evaluation for just $249.99.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button variant="contained" size="large" onClick={() => navigateAndScroll(navigate, '/book', location.pathname)}
              sx={{ backgroundColor: '#fbbf24', color: '#1e1e1e', border: '2px solid #fbbf24', boxShadow: '0 4px 20px rgba(0,0,0,0.4)', px: 4, py: 1.5, fontSize: '1.1rem', fontWeight: 600,
                '&:hover': { backgroundColor: '#f59e0b', border: '2px solid #f59e0b', boxShadow: '0 6px 25px rgba(0,0,0,0.5)' } }}>
              Book Assessment
            </Button>
            <Button variant="contained" size="large" onClick={() => navigateAndScroll(navigate, '/careers', location.pathname)}
              sx={{ backgroundColor: '#ffffff', color: '#1e1e1e', border: '2px solid #ffffff', boxShadow: '0 4px 20px rgba(0,0,0,0.4)', px: 4, py: 1.5, fontSize: '1.1rem', fontWeight: 600,
                '&:hover': { backgroundColor: '#f0f0f0', border: '2px solid #f0f0f0', boxShadow: '0 6px 25px rgba(0,0,0,0.5)' } }}>
              Join Our Team
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}

export default AboutPage;
